import React, { useState } from 'react';
import { Card } from "../../../components/tailwind/card";
import { Button } from "../../../components/tailwind/button";
import { 
  CheckCircle2, Clock, XCircle, Eye, FileText,
} from "lucide-react";
import { toHumanDate } from '../../../utils/dateTime/date';
import { TemplatePreview } from "./TemplatePreview";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from '../../../components/tailwind/dialog';
import { WaTableSkeleton } from '../../../pages/whatsapp/components/skeletons/WhatsAppPageSkeletons';

// Helper for status badge
const StatusBadge = ({ status }) => {
  const lowerStatus = status.toLowerCase();
  switch (lowerStatus) {
    case "approved":
      return (
        <div className="inline-flex items-center gap-1.5 text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-md text-xs font-normal">
          <CheckCircle2 className="h-3 w-3" /> Approved
        </div>
      );
    case "pending":
      return (
        <div className="inline-flex items-center gap-1.5 text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-md text-xs font-normal">
          <Clock className="h-3 w-3" /> Pending
        </div>
      );
    case "rejected":
      return (
        <div className="inline-flex items-center gap-1.5 text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded-md text-xs font-normal">
          <XCircle className="h-3 w-3" /> Rejected
        </div>
      );
    default:
      return null;
  }
};

export const TemplateTable = ({ templates, loading = false }) => {
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const sortedTemplates = [...(templates || [])].sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  if (loading && (!templates || templates.length === 0)) {
    return <WaTableSkeleton rows={6} cols={5} />;
  }

  return (
    <Card className="border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50 dark:bg-slate-950 border-b border-slate-100 dark:border-slate-800">
              <th className="text-left p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Template Name</th>
              <th className="text-left p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Language</th>
              <th className="text-left p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
              <th className="text-left p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Created At</th>
              <th className="text-right p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {sortedTemplates.map((template) => (
              <tr 
                key={template.id} 
                className="hover:bg-slate-50/50 dark:hover:bg-slate-800/50 transition-colors"
              >
                <td className="p-4 font-medium text-slate-900 dark:text-white">
                  {template.name}
                </td>
                <td className="p-4 text-slate-600 dark:text-slate-400 text-sm">
                  {template.language}
                </td>
                <td className="p-4">
                  <StatusBadge status={template.status} />
                </td>
                <td className="p-4 text-slate-500 text-sm">
                  {toHumanDate(template.createdAt)}
                </td>
                <td className="p-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-8 px-3 text-slate-600 hover:text-emerald-700 hover:bg-emerald-50 dark:text-slate-400"
                      onClick={() => {
                        setSelectedTemplate(template);
                        setPreviewDialogOpen(true);
                      }}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Preview
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {templates.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center py-12 text-slate-500">
                  <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p className="font-medium">No templates found</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {previewDialogOpen && selectedTemplate && (
        <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
          <DialogContent className="max-w-md max-h-[95vh] overflow-y-auto bg-white dark:bg-gray-900">
            <DialogHeader>
              <DialogTitle>Template Preview</DialogTitle>
              <DialogClose />
            </DialogHeader>
            <div className="pt-5 p-4">
              <TemplatePreview data={{
                headerType: selectedTemplate.headerText ? 'text' : 'none',
                headerText: selectedTemplate.headerText || '',
                bodyText: selectedTemplate.body || '',
                sampleValues: {},
                components: selectedTemplate.components || null,
                // indicate authentication template based on category and pass other OTP settings if available
                isAuthTemplate: String(selectedTemplate.category || '').toLowerCase() === 'authentication',
                otpType: selectedTemplate.otpType || 'copy_code',
                otpButtonText: selectedTemplate.otpButtonText || '',
                codeExpirationMinutes: selectedTemplate.codeExpirationMinutes || 10,
                addSecurityRecommendation: !!selectedTemplate.addSecurityRecommendation,
              }} mode="view" />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
};